import * as THREE from 'three';
import {
    solarSystemData,
    SCALE_CONFIG,
    calculateProportionalSize,
    calculateProportionalDistance,
} from './solarSystemData.js';
import { TrailSystem } from './TrailSystem.js';
import { CameraController } from './CameraController.js';
import { PlanetInfoSystem } from './PlanetInfoSystem.js';
import { ScaleMeter } from './ScaleMeter.js';
import { StarField } from './StarField.js';
import { SolarSystemStructures } from './SolarSystemStructures.js';

/**
 * Renderer3D - Manages the Three.js 3D scene, camera, and rendering
 */
export class Renderer3D {
    constructor(canvas) {
        this.canvas = canvas;
        this.scene = null;
        this.camera = null;
        this.renderer = null;
        this.planets = new Map();
        this.sun = null;
        this.animationId = null;

        // Trail system for heliocentric trajectories
        this.trailSystem = null;

        // Camera controller for interactive controls
        this.cameraController = null;

        // Planet info system for hover/click interactions
        this.planetInfoSystem = null;

        // Scale meter for AU reference
        this.scaleMeter = null;

        // Star field for realistic space background
        this.starField = null;

        // Solar system structures (asteroid belt, Kuiper belt, Oort cloud)
        this.solarSystemStructures = null;

        // Performance monitoring
        this.lastTime = 0;
        this.frameCount = 0;
        this.fps = 60;
        this.lastFPSUpdate = 0; // Track when FPS was last updated
    }

    /**
     * Initialize the 3D scene, camera, and renderer
     */
    initScene() {
        try {
            // Create scene
            this.scene = new THREE.Scene();
            this.scene.background = new THREE.Color(0x000011); // Dark space background

            // Create camera
            const aspect = this.canvas.clientWidth / this.canvas.clientHeight;
            // Far plane amplio para abarcar el campo de estrellas (~400k u) y la heliosfera.
            this.camera = new THREE.PerspectiveCamera(75, aspect, 0.1, 500000);
            this.camera.position.set(0, 50, 100);
            this.camera.lookAt(0, 0, 0);

            // Create renderer with WebGL error handling
            this.renderer = new THREE.WebGLRenderer({
                canvas: this.canvas,
                antialias: true,
                alpha: false,
            });

            this.renderer.setSize(this.canvas.clientWidth, this.canvas.clientHeight);
            this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
            this.renderer.shadowMap.enabled = true;
            this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;

            // Setup lighting system
            this.setupLighting();

            // Create basic geometries for sun and planets
            this.createSolarObjects();

            // Initialize trail system for heliocentric trajectories
            this.initializeTrailSystem();

            // Initialize camera controls
            this.initializeCameraControls();

            // Initialize planet info system
            this.initializePlanetInfoSystem();

            // Initialize scale meter for AU reference (Requirement 10.1, 10.2)
            this.initializeScaleMeter();

            // Initialize star field for realistic space background
            this.initializeStarField();

            // Start tracking the sun by default (Requirement 7.1)
            this.startCameraTracking('sun', 1800);

            console.log('3D Scene initialized successfully');
            return true;
        } catch (error) {
            console.error('Error initializing 3D scene:', error);
            throw new Error(`Failed to initialize 3D scene: ${error.message}`);
        }
    }

    /**
     * Setup basic lighting system
     */
    setupLighting() {
        // Ambient light for general illumination
        const ambientLight = new THREE.AmbientLight(0x404040, 0.2);
        this.scene.add(ambientLight);

        // Point light from the sun
        const sunLight = new THREE.PointLight(0xffffff, 2, 1000);
        sunLight.position.set(0, 0, 0);
        sunLight.castShadow = true;
        sunLight.shadow.mapSize.width = 2048;
        sunLight.shadow.mapSize.height = 2048;
        sunLight.shadow.camera.near = 0.5;
        sunLight.shadow.camera.far = 500;
        this.scene.add(sunLight);

        // Store reference for updates
        this.sunLight = sunLight;
    }

    /**
     * Create solar system objects with realistic data and specific materials
     */
    createSolarObjects() {
        // Create the Sun with enhanced material
        this.createSun();

        // Create all planets with specific geometries and materials
        this.createPlanets();

        console.log(`Created ${this.planets.size} planets and the sun with realistic data`);
    }

    /**
     * Create the Sun with realistic properties and proportional sizing
     */
    createSun() {
        const sunData = solarSystemData.sun;

        // Calculate proportional radius for visualization (Requirement 8.4)
        const scaledRadius = calculateProportionalSize(sunData.radius, 'sun');

        // Create high-quality sphere geometry for the sun
        const sunGeometry = new THREE.SphereGeometry(scaledRadius, 32, 32);

        // Create sun material with emissive properties
        const sunMaterial = new THREE.MeshBasicMaterial({
            color: sunData.color,
            emissive: sunData.emissiveColor,
            emissiveIntensity: sunData.emissiveIntensity,
        });

        this.sun = new THREE.Mesh(sunGeometry, sunMaterial);
        this.sun.name = sunData.name;
        this.sun.userData = {
            id: sunData.id,
            realRadius: sunData.radius,
            scaledRadius: scaledRadius,
            mass: sunData.mass,
            temperature: sunData.temperature,
        };

        this.scene.add(this.sun);

        console.log(
            `Sun created with proportional size: real=${sunData.radius}km, scaled=${scaledRadius.toFixed(2)}`
        );
    }

    /**
     * Create all planets with specific geometries and materials
     */
    createPlanets() {
        solarSystemData.planets.forEach((planetData) => {
            const planetMesh = this.createPlanetMesh(planetData);

            // Calculate proportional orbital distance (Requirement 8.1, 8.2)
            const scaledOrbitalRadius = calculateProportionalDistance(planetData.orbitalRadius);

            // Store planet data for simulation
            this.planets.set(planetData.id, {
                mesh: planetMesh,
                data: planetData,
                angle: Math.random() * Math.PI * 2, // Random starting angle
                scaledOrbitalRadius: scaledOrbitalRadius,
            });

            this.scene.add(planetMesh);

            console.log(
                `Planet ${planetData.name}: orbital distance real=${planetData.orbitalRadius}km, scaled=${scaledOrbitalRadius.toFixed(2)}`
            );
        });
    }

    /**
     * Create a specific planet mesh with appropriate geometry and material
     * @param {Object} planetData - Planet data from solarSystemData
     * @returns {THREE.Mesh} Planet mesh
     */
    createPlanetMesh(planetData) {
        // Calculate scaled radius for visualization
        const scaledRadius = this.calculateScaledPlanetRadius(planetData);

        // Create geometry based on planet characteristics
        const geometry = this.createPlanetGeometry(planetData, scaledRadius);

        // Create material based on planet properties
        const material = this.createPlanetMaterial(planetData);

        // Create the mesh
        const mesh = new THREE.Mesh(geometry, material);
        mesh.name = planetData.name;
        mesh.castShadow = true;
        mesh.receiveShadow = true;

        // Calculate proportional orbital distance for userData
        const scaledOrbitalRadius = calculateProportionalDistance(planetData.orbitalRadius);

        // Store additional data in userData
        mesh.userData = {
            id: planetData.id,
            realRadius: planetData.radius,
            scaledRadius: scaledRadius,
            realOrbitalRadius: planetData.orbitalRadius,
            scaledOrbitalRadius: scaledOrbitalRadius,
            mass: planetData.mass,
            orbitalRadius: planetData.orbitalRadius, // Keep for compatibility
            orbitalPeriod: planetData.orbitalPeriod,
            rotationPeriod: planetData.rotationPeriod,
            hasRings: planetData.hasRings || false,
            moons: planetData.moons || 0,
            surfaceGravity: planetData.surfaceGravity,
            atmosphereComposition: planetData.atmosphereComposition,
            albedo: planetData.albedo,
            orbitalEccentricity: planetData.orbitalEccentricity,
            orbitalInclination: planetData.orbitalInclination,
        };

        // Add rings for planets that have them
        if (planetData.hasRings) {
            this.addPlanetRings(mesh, planetData);
        }

        return mesh;
    }

    /**
     * Calculate proportional radius for planet visualization
     * @param {Object} planetData - Planet data
     * @returns {number} Proportionally scaled radius
     */
    calculateScaledPlanetRadius(planetData) {
        // Use the new proportional sizing system (Requirements 8.1, 8.2, 8.3)
        const scaledRadius = calculateProportionalSize(planetData.radius, 'planet');

        console.log(
            `Planet ${planetData.name}: real=${planetData.radius}km, scaled=${scaledRadius.toFixed(2)}`
        );

        return scaledRadius;
    }

    /**
     * Create planet geometry based on characteristics
     * @param {Object} planetData - Planet data
     * @param {number} scaledRadius - Scaled radius for visualization
     * @returns {THREE.BufferGeometry} Planet geometry
     */
    createPlanetGeometry(planetData, scaledRadius) {
        // Higher detail for larger planets
        const segments = planetData.radius > 20000 ? 32 : 16; // Gas giants get more detail

        return new THREE.SphereGeometry(scaledRadius, segments, segments);
    }

    /**
     * Create planet material based on properties
     * @param {Object} planetData - Planet data
     * @returns {THREE.Material} Planet material
     */
    createPlanetMaterial(planetData) {
        const materialProps = {
            color: planetData.color,
            roughness: this.calculateRoughness(planetData),
            metalness: this.calculateMetalness(planetData),
            // Añadir emisividad para mantener los planetas visibles con sus colores
            emissive: planetData.color,
            emissiveIntensity: 0.3, // Intensidad emisiva para mantener visibilidad
        };

        // Use different material types based on planet characteristics
        if (
            planetData.atmosphereComposition &&
            planetData.atmosphereComposition !== 'Prácticamente inexistente'
        ) {
            // Planets with atmosphere use standard material with emissive properties
            return new THREE.MeshStandardMaterial(materialProps);
        } else {
            // Airless bodies use Lambert material with emissive properties for visibility
            return new THREE.MeshLambertMaterial({
                color: planetData.color,
                emissive: planetData.color,
                emissiveIntensity: 0.2, // Menor intensidad para cuerpos sin atmósfera
            });
        }
    }

    /**
     * Calculate material roughness based on planet properties
     * @param {Object} planetData - Planet data
     * @returns {number} Roughness value (0-1)
     */
    calculateRoughness(planetData) {
        // Gas giants are smoother due to atmospheric effects
        if (planetData.radius > 20000) {
            return 0.3;
        }

        // Rocky planets with atmosphere are smoother
        if (
            planetData.atmosphereComposition &&
            planetData.atmosphereComposition !== 'Prácticamente inexistente'
        ) {
            return 0.6;
        }

        // Airless rocky bodies are rougher
        return 0.9;
    }

    /**
     * Calculate material metalness based on planet properties
     * @param {Object} planetData - Planet data
     * @returns {number} Metalness value (0-1)
     */
    calculateMetalness(planetData) {
        // Most planets have low metalness (non-metallic surfaces)
        if (planetData.id === 'mercury') {
            return 0.3; // Mercury has a large iron core
        }

        return 0.1; // Low metalness for most planets
    }

    /**
     * Add ring system to a planet
     * @param {THREE.Mesh} planetMesh - Planet mesh
     * @param {Object} planetData - Planet data
     */
    addPlanetRings(planetMesh, planetData) {
        if (!planetData.ringSystem) return;

        const innerRadius = planetData.ringSystem.innerRadius * SCALE_CONFIG.sizeScale;
        const outerRadius = planetData.ringSystem.outerRadius * SCALE_CONFIG.sizeScale;

        // Create ring geometry
        const ringGeometry = new THREE.RingGeometry(innerRadius, outerRadius, 32);

        // Create ring material
        const ringMaterial = new THREE.MeshBasicMaterial({
            color: 0xcccccc,
            side: THREE.DoubleSide,
            transparent: true,
            opacity: 0.6,
        });

        const ringMesh = new THREE.Mesh(ringGeometry, ringMaterial);
        ringMesh.rotation.x = Math.PI / 2; // Rotate to horizontal

        // Add rings as child of planet
        planetMesh.add(ringMesh);
    }

    /**
     * Initialize the trail system for heliocentric trajectories
     */
    initializeTrailSystem() {
        try {
            // Create trail system with optimized point limit
            this.trailSystem = new TrailSystem(1000);

            // Initialize with the current scene
            this.trailSystem.initializeWithScene(this.scene);

            console.log('Trail system initialized successfully');
        } catch (error) {
            console.error('Error initializing trail system:', error);
            // Continue without trails if initialization fails
            this.trailSystem = null;
        }
    }

    /**
     * Initialize camera controls for interactive navigation
     */
    initializeCameraControls() {
        try {
            // Create camera controller
            this.cameraController = new CameraController(this.camera, this.canvas);

            // Set references to solar system objects for POV mode
            this.cameraController.setSolarSystemObjects(this.sun, this.planets);

            // Configure zoom limits appropriate for solar system scale (increased for Oort cloud)
            this.cameraController.setZoomLimits(5, 50000);

            // Set rotation limits to prevent camera flipping
            this.cameraController.setRotationLimits(
                0.1, // Minimum polar angle (prevent going under the plane)
                Math.PI - 0.1, // Maximum polar angle (prevent going under the plane)
                -Infinity, // No azimuth limits (full horizontal rotation)
                Infinity
            );

            // Enable smooth damping for better user experience
            this.cameraController.setDamping(true, 0.05);

            // Optimize for mobile devices if detected
            this.cameraController.optimizeForMobile();

            // Focus on the solar system center initially
            this.cameraController.focusOnObject(new THREE.Vector3(0, 0, 0));

            console.log('Camera controls initialized successfully');
        } catch (error) {
            console.error('Error initializing camera controls:', error);
            // Continue without interactive controls if initialization fails
            this.cameraController = null;
        }
    }

    /**
     * Initialize planet info system for hover/click interactions
     */
    initializePlanetInfoSystem() {
        try {
            // Create planet info system with callback for planet selection
            const onPlanetSelected = (planetId, planetMesh) => {
                // Start tracking the selected planet (Requirement 7.2)
                this.startCameraTracking(planetId);

                // Update UI to reflect the tracking change
                if (window.solarSimulator && window.solarSimulator.getUIController) {
                    const uiController = window.solarSimulator.getUIController();
                    if (uiController && uiController.updateTrackingUI) {
                        uiController.updateTrackingUI();
                    }
                }

                console.log(`Planet selected and camera tracking started: ${planetId}`);
            };

            this.planetInfoSystem = new PlanetInfoSystem(
                this,
                this.camera,
                this.canvas,
                onPlanetSelected
            );

            console.log('Planet info system initialized successfully');
        } catch (error) {
            console.error('Error initializing planet info system:', error);
            // Continue without planet info system if initialization fails
            this.planetInfoSystem = null;
        }
    }

    /**
     * Initialize scale meter for AU reference display (Requirement 10.1, 10.2, 10.3, 10.4)
     */
    initializeScaleMeter() {
        try {
            // Create scale meter with camera reference and canvas container
            this.scaleMeter = new ScaleMeter(this.camera, document.body);

            console.log('Scale meter initialized successfully');
        } catch (error) {
            console.error('Error initializing scale meter:', error);
            // Continue without scale meter if initialization fails
            this.scaleMeter = null;
        }
    }

    /**
     * Initialize star field for realistic space background
     */
    initializeStarField() {
        try {
            // Create star field with 8000 stars for good balance of realism and performance
            // Stars will be positioned at realistic interstellar distances
            this.starField = new StarField(8000);

            // Configure star field for realistic appearance
            // Configure star field for realistic appearance with galactic structure
            // Positioned at realistic interstellar distances with proper galactic orientation
            this.starField.setConfig({
                minDistance: 100000, // Much farther for realistic scale
                maxDistance: 400000, // Very distant stars
                minSize: 0.5, // Small point-like stars
                maxSize: 2.0, // Maximum size for bright stars
                minBrightness: 0.4, // Adequate minimum brightness
                maxBrightness: 1.0, // Full brightness for bright stars
                colorVariation: true, // Realistic star colors
                twinkling: false, // Disabled by default for performance
                galacticStructure: true, // Enable realistic Milky Way structure
                galacticMotion: false, // Subtle galactic rotation (optional)
            });

            // Create the star field with realistic galactic distribution
            this.starField.createRealisticDistribution(this.scene);

            // Start with star field visible by default
            this.starField.show();

            console.log('Star field initialized successfully');
        } catch (error) {
            console.error('Error initializing star field:', error);
            // Continue without star field if initialization fails
            this.starField = null;
        }

        // Initialize solar system structures
        try {
            // Pass current sun position if available
            const currentSunPosition = this.sun ? this.sun.position : null;
            this.solarSystemStructures = new SolarSystemStructures(this.scene, currentSunPosition);
            console.log('Solar system structures initialized successfully');
        } catch (error) {
            console.error('Error initializing solar system structures:', error);
            this.solarSystemStructures = null;
        }
    }

    /**
     * Update planet positions using heliocentric simulation data
     * @param {Object} planetPositions - Map of planet positions from SimulationEngine
     * @param {Object} solarPosition - Sun position from SimulationEngine
     * @param {Object} planetRotations - Map of planet rotations from SimulationEngine
     * @param {boolean} isPaused - Whether the simulation is paused
     */
    updatePlanets(
        planetPositions,
        solarPosition,
        planetRotations = null,
        isPaused = false,
        currentTime = 0
    ) {
        if (!this.planets || !planetPositions) return;

        // Initialize trail positions on first update to make trails visible immediately
        if (this.trailSystem && !this.trailSystem.initialized) {
            this.trailSystem.initializeTrailPositions(planetPositions);
            this.trailSystem.initialized = true;
        }

        // Update sun position
        if (this.sun && solarPosition) {
            this.sun.position.set(solarPosition.x, solarPosition.y, solarPosition.z);

            // Update sun light position to follow the sun
            if (this.sunLight) {
                this.sunLight.position.copy(this.sun.position);
            }

            // Update solar system structures to follow the sun y hacerlas orbitar (cinturones)
            if (this.solarSystemStructures) {
                this.solarSystemStructures.updateSunPosition(this.sun.position);
                this.solarSystemStructures.updateRotation(currentTime);
            }
        }

        // Update planet positions and rotations using simulation data
        this.planets.forEach((planet, planetId) => {
            const position = planetPositions[planetId];
            if (position) {
                // Set planet position from simulation engine
                planet.mesh.position.set(position.x, position.y, position.z);

                // Update trail system with new position
                if (this.trailSystem) {
                    this.trailSystem.addPoint(planetId, position);
                }

                // Update planet rotation using simulation engine data
                // Only update rotation if simulation is not paused (Requirement 9.4)
                if (!isPaused && planetRotations && planetRotations[planetId] !== undefined) {
                    // Set rotation directly from simulation engine calculation
                    // This ensures synchronization with simulation speed (Requirement 9.2)
                    planet.mesh.rotation.y = planetRotations[planetId];
                }
            }
        });
    }

    /**
     * Update planet positions with basic circular motion (fallback method)
     * @param {number} deltaTime - Time delta in milliseconds
     * @param {boolean} isPaused - Whether the simulation is paused
     */
    updatePlanetsBasic(deltaTime, isPaused = false) {
        if (!this.planets) return;

        this.planets.forEach((planet, planetId) => {
            // Only update if not paused
            if (!isPaused) {
                // Basic circular motion using real orbital data
                const orbitalSpeed = (2 * Math.PI) / planet.data.orbitalPeriod; // radians per day
                planet.angle += deltaTime * 0.001 * orbitalSpeed * 365; // Convert to appropriate time scale

                // Position planet using scaled orbital radius
                planet.mesh.position.x = Math.cos(planet.angle) * planet.scaledOrbitalRadius;
                planet.mesh.position.z = Math.sin(planet.angle) * planet.scaledOrbitalRadius;
                planet.mesh.position.y = 0; // Keep in orbital plane for now

                // Add realistic planet rotation (Requirements 9.1, 9.2, 9.3)
                if (planet.data.rotationPeriod) {
                    // Calculate rotation speed in radians per day
                    const rotationSpeed = (2 * Math.PI) / planet.data.rotationPeriod;

                    // Convert deltaTime to days and apply rotation
                    // Note: rotationPeriod is negative for retrograde planets (Venus, Uranus)
                    const deltaTimeInDays = ((deltaTime * 0.001) / 86400) * 365; // Convert ms to days
                    planet.mesh.rotation.y += rotationSpeed * deltaTimeInDays;
                }
            }
        });
    }

    /**
     * Handle window resize
     */
    resize(width, height) {
        if (!this.camera || !this.renderer) return;

        this.camera.aspect = width / height;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(width, height);
    }

    /**
     * Render the scene
     */
    render() {
        if (!this.renderer || !this.scene || !this.camera) return;

        try {
            // Update camera controls
            if (this.cameraController) {
                this.cameraController.update();
            }

            // Update scale meter with current camera distance (Requirement 10.3)
            if (this.scaleMeter && this.cameraController) {
                const cameraDistance = this.camera.position.distanceTo(
                    this.cameraController.controls.target
                );
                this.scaleMeter.update(cameraDistance);
            }

            // Update star field (twinkling) y centrarlo en la cámara (skybox): evita el paralaje
            // de las estrellas al volar el sistema por la galaxia y que se salgan de rango.
            if (this.starField) {
                this.starField.update(0.016); // Approximate 60fps delta time
                this.starField.centerOn(this.camera.position);
            }

            // Solar system structures are updated in updatePlanets() method

            this.renderer.render(this.scene, this.camera);
            this.updateFPS();
        } catch (error) {
            console.error('Rendering error:', error);
            throw error;
        }
    }

    /**
     * Start the render loop
     */
    startRenderLoop() {
        const animate = (currentTime) => {
            this.animationId = requestAnimationFrame(animate);

            const deltaTime = currentTime - this.lastTime;
            this.lastTime = currentTime;

            // Update planets with basic motion
            this.updatePlanets(deltaTime);

            // Render the scene
            this.render();
        };

        animate(0);
    }

    /**
     * Stop the render loop
     */
    stopRenderLoop() {
        if (this.animationId) {
            cancelAnimationFrame(this.animationId);
            this.animationId = null;
        }
    }

    /**
     * Update FPS counter
     */
    updateFPS() {
        this.frameCount++;
        const now = performance.now();

        if (now - this.lastFPSUpdate > 1000) {
            this.fps = Math.round((this.frameCount * 1000) / (now - this.lastFPSUpdate));
            this.frameCount = 0;
            this.lastFPSUpdate = now;
        }
    }

    /**
     * Get current FPS
     */
    getFPS() {
        return this.fps;
    }

    /**
     * Get the Three.js scene
     */
    getScene() {
        return this.scene;
    }

    /**
     * Get the camera
     */
    getCamera() {
        return this.camera;
    }

    /**
     * Get the renderer
     */
    getRenderer() {
        return this.renderer;
    }

    /**
     * Get the trail system
     */
    getTrailSystem() {
        return this.trailSystem;
    }

    /**
     * Set trail visibility
     * @param {boolean} visible - Whether trails should be visible
     */
    setTrailsVisible(visible) {
        if (this.trailSystem) {
            this.trailSystem.setVisible(visible);
        }
    }

    /**
     * Clear all trails
     */
    clearTrails() {
        if (this.trailSystem) {
            this.trailSystem.clearTrails();
        }
    }

    /**
     * Set trail length (maximum points per trail)
     * @param {number} length - Maximum number of points per trail
     */
    setTrailLength(length) {
        if (this.trailSystem) {
            this.trailSystem.setTrailLength(length);
        }
    }

    /**
     * Get trail system statistics
     * @returns {Object} Trail statistics
     */
    getTrailStatistics() {
        return this.trailSystem ? this.trailSystem.getStatistics() : null;
    }

    /**
     * Get the camera controller
     * @returns {CameraController} Camera controller instance
     */
    getCameraController() {
        return this.cameraController;
    }

    /**
     * Enable camera controls
     */
    enableCameraControls() {
        if (this.cameraController) {
            this.cameraController.enableControls();
        }
    }

    /**
     * Disable camera controls
     */
    disableCameraControls() {
        if (this.cameraController) {
            this.cameraController.disableControls();
        }
    }

    /**
     * Get the scale meter instance
     * @returns {ScaleMeter} Scale meter instance
     */
    getScaleMeter() {
        return this.scaleMeter;
    }

    /**
     * Show the scale meter
     */
    showScaleMeter() {
        if (this.scaleMeter) {
            this.scaleMeter.show();
        }
    }

    /**
     * Hide the scale meter
     */
    hideScaleMeter() {
        if (this.scaleMeter) {
            this.scaleMeter.hide();
        }
    }

    /**
     * Toggle scale meter visibility
     */
    toggleScaleMeter() {
        if (this.scaleMeter) {
            this.scaleMeter.toggle();
        }
    }

    /**
     * Check if scale meter is visible
     * @returns {boolean} True if scale meter is visible
     */
    isScaleMeterVisible() {
        return this.scaleMeter ? this.scaleMeter.isShowing() : false;
    }

    /**
     * Get the star field instance
     * @returns {StarField} Star field instance
     */
    getStarField() {
        return this.starField;
    }

    /**
     * Show the star field
     */
    showStarField() {
        if (this.starField) {
            this.starField.show();
        }
    }

    /**
     * Hide the star field
     */
    hideStarField() {
        if (this.starField) {
            this.starField.hide();
        }
    }

    /**
     * Toggle star field visibility
     */
    toggleStarField() {
        if (this.starField) {
            this.starField.toggle();
        }
    }

    /**
     * Check if star field is visible
     * @returns {boolean} True if star field is visible
     */
    isStarFieldVisible() {
        return this.starField ? this.starField.isStarFieldVisible() : false;
    }

    /**
     * Enable or disable star twinkling effect
     * @param {boolean} enabled - Whether twinkling should be enabled
     */
    setStarTwinkling(enabled) {
        if (this.starField) {
            this.starField.setTwinkling(enabled);
        }
    }

    /**
     * Enable or disable galactic structure simulation
     * @param {boolean} enabled - Whether to use realistic galactic distribution
     */
    setGalacticStructure(enabled) {
        if (this.starField) {
            this.starField.setGalacticStructure(enabled);
        }
    }

    /**
     * Check if galactic structure is enabled
     * @returns {boolean} True if galactic structure is enabled
     */
    isGalacticStructureEnabled() {
        return this.starField ? this.starField.isGalacticStructureEnabled() : false;
    }

    /**
     * Show asteroid belt
     */
    showAsteroidBelt() {
        if (this.solarSystemStructures) {
            // Ensure structures have current sun position before showing
            if (this.sun) {
                console.log(
                    'Sun position when showing asteroid belt:',
                    this.sun.position.x,
                    this.sun.position.y,
                    this.sun.position.z
                );
                this.solarSystemStructures.updateSunPosition(this.sun.position);
            }
            this.solarSystemStructures.showAsteroidBelt();
        }
    }

    /**
     * Hide asteroid belt
     */
    hideAsteroidBelt() {
        if (this.solarSystemStructures) {
            this.solarSystemStructures.hideAsteroidBelt();
        }
    }

    /**
     * Show Kuiper belt
     */
    showKuiperBelt() {
        if (this.solarSystemStructures) {
            // Ensure structures have current sun position before showing
            if (this.sun) {
                this.solarSystemStructures.updateSunPosition(this.sun.position);
            }
            this.solarSystemStructures.showKuiperBelt();
        }
    }

    /**
     * Hide Kuiper belt
     */
    hideKuiperBelt() {
        if (this.solarSystemStructures) {
            this.solarSystemStructures.hideKuiperBelt();
        }
    }

    /**
     * Show heliosphere (termination shock + heliopause)
     */
    showHeliosphere() {
        if (this.solarSystemStructures) {
            if (this.sun) {
                this.solarSystemStructures.updateSunPosition(this.sun.position);
            }
            this.solarSystemStructures.showHeliosphere();
        }
    }

    /**
     * Hide heliosphere
     */
    hideHeliosphere() {
        if (this.solarSystemStructures) {
            this.solarSystemStructures.hideHeliosphere();
        }
    }

    /**
     * Get solar system structures visibility states
     * @returns {Object} Visibility states for all structures
     */
    getStructuresVisibility() {
        return this.solarSystemStructures
            ? this.solarSystemStructures.getVisibilityStates()
            : {
                  asteroidBelt: false,
                  kuiperBelt: false,
                  oortCloud: false,
              };
    }

    /**
     * Focus camera on a specific planet or object
     * @param {string} planetId - ID of planet to focus on, or 'sun' for the sun
     * @param {number} distance - Optional distance from target
     */
    focusOnPlanet(planetId, distance = null) {
        if (!this.cameraController) return;

        const target = this.getTargetObject(planetId);

        if (target) {
            // Calculate appropriate distance if not provided
            if (distance === null) {
                const boundingBox = new THREE.Box3().setFromObject(target);
                const size = boundingBox.getSize(new THREE.Vector3());
                distance = Math.max(size.x, size.y, size.z) * 3;
            }

            this.cameraController.focusOnObject(target, distance);
        }
    }

    /**
     * Resuelve el objeto 3D (malla) de un cuerpo por su id: el Sol o un planeta.
     * Centraliza la resolución usada por focusOnPlanet, selectPlanet y startCameraTracking.
     * @param {string} objectId - 'sun' o el id de un planeta
     * @returns {THREE.Object3D|null} La malla del objeto, o null si no existe
     */
    getTargetObject(objectId) {
        if (objectId === 'sun') {
            return this.sun || null;
        }
        const planetData = this.planets.get(objectId);
        return planetData ? planetData.mesh : null;
    }

    /**
     * Reset camera to default position
     */
    resetCamera() {
        if (this.cameraController) {
            this.cameraController.reset();
        }
    }

    /**
     * Aplica un preset de cámara. TODOS los presets activan SEGUIMIENTO del objeto (Sol o
     * Tierra): como el sistema vuela por la galaxia (movimiento heliocéntrico), una vista
     * estática se quedaría atrás al instante; siguiendo al objeto la vista permanece centrada.
     * La eclíptica es el plano XY (normal Z): "cenital" mira a lo largo de Z (órbitas como
     * círculos) y "de canto" mira dentro del plano.
     * @param {string} preset - 'top' | 'front' | 'earth45' | 'overview'
     */
    applyCameraPreset(preset) {
        if (!this.cameraController) return;

        // Coloca la cámara en center+offset, apunta al center y ACTIVA el seguimiento del
        // objeto a esa distancia, de modo que el offset relativo se mantiene mientras se mueve.
        const trackWithOffset = (objectId, offset) => {
            const obj = this.getTargetObject(objectId);
            if (!obj) return;
            const center = obj.position.clone();
            this.cameraController.reset(center.clone().add(offset), center.clone());
            // Seguimiento INMEDIATO (sin transición de 1 s): con el movimiento heliocéntrico,
            // una transición suave dejaría la cámara atrás y los planetas saldrían de plano.
            this.startCameraTracking(objectId, offset.length(), false);
        };

        switch (preset) {
            case 'top':
                // Cenital / clásica: de frente a la eclíptica (a lo largo de la normal Z), siguiendo al Sol
                trackWithOffset('sun', new THREE.Vector3(0, 0, 700));
                break;

            case 'front':
                // De canto: dentro del plano de la eclíptica (órbitas de perfil), siguiendo al Sol
                trackWithOffset('sun', new THREE.Vector3(0, 700, 40));
                break;

            case 'earth45': {
                // Siguiendo a la Tierra desde 45° sobre la eclíptica
                const d = 18;
                trackWithOffset('earth', new THREE.Vector3(0, d * Math.SQRT1_2, d * Math.SQRT1_2));
                break;
            }

            case 'heliosphere':
                // Heliosfera (morro ~6000 u, cola ~21000 u): vista desde arriba para apreciar
                // la forma de gota completa (morro hacia +Z, cola hacia -Z).
                trackWithOffset('sun', new THREE.Vector3(0, 25000, 8000));
                break;

            case 'overview':
            default:
                // Vista general amplia en perspectiva 3/4, siguiendo al Sol
                trackWithOffset('sun', new THREE.Vector3(60, 250, 450));
                break;
        }

        console.log(`Camera preset applied: ${preset}`);
    }

    /**
     * Set camera auto rotation
     * @param {boolean} enabled - Whether auto rotation should be enabled
     * @param {number} speed - Auto rotation speed
     */
    setCameraAutoRotate(enabled, speed = 2.0) {
        if (this.cameraController) {
            this.cameraController.setAutoRotate(enabled, speed);
        }
    }

    /**
     * Get current camera state
     * @returns {Object} Camera state information
     */
    getCameraState() {
        return this.cameraController ? this.cameraController.getCameraState() : null;
    }

    /**
     * Get the planet info system
     * @returns {PlanetInfoSystem} Planet info system instance
     */
    getPlanetInfoSystem() {
        return this.planetInfoSystem;
    }

    /**
     * Enable planet info system
     */
    enablePlanetInfo() {
        if (this.planetInfoSystem) {
            this.planetInfoSystem.enable();
        }
    }

    /**
     * Disable planet info system
     */
    disablePlanetInfo() {
        if (this.planetInfoSystem) {
            this.planetInfoSystem.disable();
        }
    }

    /**
     * Get currently selected planet
     * @returns {THREE.Object3D|null} Selected planet mesh
     */
    getSelectedPlanet() {
        return this.planetInfoSystem ? this.planetInfoSystem.getSelectedPlanet() : null;
    }

    /**
     * Select a planet programmatically
     * @param {string} planetId - ID of planet to select
     */
    selectPlanet(planetId) {
        if (!this.planetInfoSystem) return;

        const targetMesh = this.getTargetObject(planetId);

        if (targetMesh) {
            this.planetInfoSystem.selectPlanet(targetMesh);
        }
    }

    /**
     * Deselect current planet
     */
    deselectPlanet() {
        if (this.planetInfoSystem) {
            this.planetInfoSystem.deselectPlanet();
        }
    }

    /**
     * Start tracking a celestial object with the camera
     * @param {string} objectId - ID of object to track ('sun' or planet ID)
     * @param {number} distance - Optional distance to maintain
     */
    startCameraTracking(objectId, distance = null, smoothTransition = true) {
        if (!this.cameraController) {
            console.warn('Camera controller not available for tracking');
            return;
        }

        const targetObject = this.getTargetObject(objectId);
        let defaultDistance = 100;

        if (objectId === 'sun') {
            defaultDistance = 1800; // Closer distance for the sun
        } else {
            const planetData = this.planets.get(objectId);
            if (planetData) {
                // Calculate appropriate distance based on planet size, but closer
                defaultDistance = Math.max(20, planetData.data.radius * 0.005);
            }
        }

        if (targetObject) {
            const trackingDistance = distance || defaultDistance;
            this.cameraController.startTracking(targetObject, trackingDistance, smoothTransition);
            console.log(`Started tracking ${objectId} at distance ${trackingDistance}`);
        } else {
            console.warn(`Cannot track object: ${objectId} not found`);
        }
    }

    /**
     * Stop camera tracking
     */
    stopCameraTracking() {
        if (this.cameraController) {
            this.cameraController.stopTracking(true);
        }
    }

    /**
     * Check if camera is currently tracking an object
     * @returns {boolean} True if tracking is active
     */
    isCameraTracking() {
        return this.cameraController ? this.cameraController.isTracking() : false;
    }

    /**
     * Get the currently tracked object
     * @returns {string|null} ID of currently tracked object
     */
    getTrackedObjectId() {
        if (!this.cameraController || !this.cameraController.isTracking()) {
            return null;
        }

        const trackedObject = this.cameraController.getTrackedObject();
        if (!trackedObject) return null;

        // Check if it's the sun
        if (trackedObject === this.sun) {
            return 'sun';
        }

        // Check if it's a planet
        for (const [planetId, planetData] of this.planets) {
            if (planetData.mesh === trackedObject) {
                return planetId;
            }
        }

        return null;
    }

    /**
     * Set tracking distance for current target
     * @param {number} distance - New tracking distance
     */
    setTrackingDistance(distance) {
        if (this.cameraController) {
            this.cameraController.setTrackingDistance(distance);
        }
    }

    /**
     * Get current tracking distance
     * @returns {number} Current tracking distance
     */
    getTrackingDistance() {
        if (this.cameraController) {
            return this.cameraController.getTrackingDistance();
        }
        return 1800; // Default distance
    }

    /**
     * Get camera tracking state
     * @returns {Object} Tracking state information
     */
    getCameraTrackingState() {
        if (!this.cameraController) return null;

        const trackingState = this.cameraController.getTrackingState();
        return {
            ...trackingState,
            trackedObjectId: this.getTrackedObjectId(),
        };
    }

    /**
     * Get proportional sizing information for all celestial bodies
     * @returns {Object} Proportional sizing data
     */
    getProportionalSizingInfo() {
        const info = {
            sun: null,
            planets: {},
            proportions: {},
        };

        // Get sun information
        if (this.sun) {
            info.sun = {
                name: this.sun.name,
                realRadius: this.sun.userData.realRadius,
                scaledRadius: this.sun.userData.scaledRadius,
                geometry: this.sun.geometry,
            };
        }

        // Get planet information
        this.planets.forEach((planetObj, planetId) => {
            const mesh = planetObj.mesh;
            info.planets[planetId] = {
                name: mesh.name,
                realRadius: mesh.userData.realRadius,
                scaledRadius: mesh.userData.scaledRadius,
                realOrbitalRadius: mesh.userData.realOrbitalRadius,
                scaledOrbitalRadius: mesh.userData.scaledOrbitalRadius,
                geometry: mesh.geometry,
            };
        });

        // Calculate proportions between bodies
        if (info.sun) {
            const sunScaledRadius = info.sun.scaledRadius;

            Object.keys(info.planets).forEach((planetId) => {
                const planet = info.planets[planetId];
                info.proportions[planetId] = {
                    realRatioToSun: planet.realRadius / info.sun.realRadius,
                    scaledRatioToSun: planet.scaledRadius / sunScaledRadius,
                    proportionMaintained:
                        Math.abs(
                            planet.scaledRadius / sunScaledRadius -
                                planet.realRadius / info.sun.realRadius
                        ) < 0.1, // Allow 10% deviation for visibility adjustments
                };
            });
        }

        return info;
    }

    /**
     * Validate that proportional sizing is working correctly
     * @returns {Object} Validation results
     */
    validateProportionalSizing() {
        const sizingInfo = this.getProportionalSizingInfo();
        const errors = [];
        const warnings = [];

        if (!sizingInfo.sun) {
            errors.push('Sun not found in scene');
            return { isValid: false, errors, warnings };
        }

        // Check that planets are ordered correctly by size
        const planetsByRealSize = Object.entries(sizingInfo.planets).sort(
            ([, a], [, b]) => b.realRadius - a.realRadius
        );

        const planetsByScaledSize = Object.entries(sizingInfo.planets).sort(
            ([, a], [, b]) => b.scaledRadius - a.scaledRadius
        );

        // Verify major planets maintain relative order
        const majorPlanets = ['jupiter', 'saturn', 'neptune', 'uranus'];
        for (let i = 0; i < majorPlanets.length - 1; i++) {
            const current = majorPlanets[i];
            const next = majorPlanets[i + 1];

            if (sizingInfo.planets[current] && sizingInfo.planets[next]) {
                const currentScaled = sizingInfo.planets[current].scaledRadius;
                const nextScaled = sizingInfo.planets[next].scaledRadius;
                const currentReal = sizingInfo.planets[current].realRadius;
                const nextReal = sizingInfo.planets[next].realRadius;

                if (currentReal > nextReal && currentScaled <= nextScaled) {
                    warnings.push(`Size order issue: ${current} should be larger than ${next}`);
                }
            }
        }

        // Check minimum size requirements
        Object.entries(sizingInfo.planets).forEach(([planetId, planet]) => {
            if (planet.scaledRadius < SCALE_CONFIG.proportionalSizing.minVisibleSize) {
                warnings.push(`Planet ${planetId} may be too small to see clearly`);
            }
        });

        // Check sun visibility
        const largestPlanetSize = Math.max(
            ...Object.values(sizingInfo.planets).map((p) => p.scaledRadius)
        );
        if (sizingInfo.sun.scaledRadius < largestPlanetSize) {
            warnings.push('Sun is smaller than the largest planet in visualization');
        }

        return {
            isValid: errors.length === 0,
            errors,
            warnings,
            sizingInfo,
        };
    }

    /**
     * Get size comparison data for educational purposes
     * @returns {Object} Size comparison data
     */
    getSizeComparison() {
        const sizingInfo = this.getProportionalSizingInfo();
        const comparison = {
            realSizes: {},
            scaledSizes: {},
            ratios: {},
        };

        // Add sun data
        if (sizingInfo.sun) {
            comparison.realSizes.sun = sizingInfo.sun.realRadius;
            comparison.scaledSizes.sun = sizingInfo.sun.scaledRadius;
        }

        // Add planet data
        Object.entries(sizingInfo.planets).forEach(([planetId, planet]) => {
            comparison.realSizes[planetId] = planet.realRadius;
            comparison.scaledSizes[planetId] = planet.scaledRadius;
        });

        // Calculate ratios
        comparison.ratios = sizingInfo.proportions;

        return comparison;
    }

    /**
     * Cleanup resources
     */
    dispose() {
        this.stopRenderLoop();

        // Dispose planet info system
        if (this.planetInfoSystem) {
            this.planetInfoSystem.dispose();
            this.planetInfoSystem = null;
        }

        // Dispose scale meter
        if (this.scaleMeter) {
            this.scaleMeter.dispose();
            this.scaleMeter = null;
        }

        // Dispose star field
        if (this.starField) {
            this.starField.dispose();
            this.starField = null;
        }

        // Dispose solar system structures
        if (this.solarSystemStructures) {
            this.solarSystemStructures.dispose();
            this.solarSystemStructures = null;
        }

        // Dispose camera controller
        if (this.cameraController) {
            this.cameraController.dispose();
            this.cameraController = null;
        }

        // Dispose trail system
        if (this.trailSystem) {
            this.trailSystem.dispose();
            this.trailSystem = null;
        }

        if (this.renderer) {
            this.renderer.dispose();
        }

        // Dispose geometries and materials
        this.scene?.traverse((object) => {
            if (object.geometry) {
                object.geometry.dispose();
            }
            if (object.material) {
                if (Array.isArray(object.material)) {
                    object.material.forEach((material) => material.dispose());
                } else {
                    object.material.dispose();
                }
            }
        });
    }
}
